import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { memoize } from "lodash";
import { useMemo } from "react";
import { debugLog } from "../../../../logger";
import getEventsV3, { url } from "../../requests/getEventsV3";
import { cacheTimeMs, staleTimeMs } from "../../Shared";
import { generateCumulativeDailyNetDeposits, } from "../../utilities/getEvents";
import useGetDatesForTimeFrame from "../useGetDatesForTimeFrame";
export const memoizedGenerateCumulativeDailyNetDeposits = memoize(generateCumulativeDailyNetDeposits, (api, start, end) => {
    // only memoize in 1 hour chunks (this is `HH`) ex: https://date-fns.org/v2.29.3/docs/format
    const now = format(new Date(), "P:HH");
    const accountList = api
        .map((a) => `${a.accountId}-${a.transactions.length}`)
        .join(",");
    return `${start}-${end}-${now}-${accountList}`;
});
export default function useGetCumulativeDailyNetDeposits(groupId, timeFrame, customTimeFrame, personId, authConfig) {
    const requestEnabled = groupId !== null && authConfig !== null && personId !== null;
    const query = useQuery({
        queryKey: groupId === null ? undefined : [url(groupId)],
        queryFn: async () => groupId === null || authConfig === null
            ? undefined
            : getEventsV3(groupId, authConfig),
        enabled: requestEnabled,
        cacheTime: cacheTimeMs,
        staleTime: staleTimeMs,
    });
    const timeFrameDates = useGetDatesForTimeFrame(groupId, timeFrame, customTimeFrame, personId, authConfig);
    const result = useMemo(() => {
        if (timeFrameDates.isLoading || timeFrameDates.hasError) {
            return timeFrameDates;
        }
        if (query.data === undefined && query.isLoading) {
            return { data: undefined, isLoading: true, hasError: false };
        }
        if (groupId === null) {
            return { data: undefined, isLoading: true, hasError: false };
        }
        if (query.data === undefined && query.isError) {
            debugLog("useGetCumulativeDailyNetDeposits", `useQuery error with ${timeFrame} for ${groupId} - ${query.error}`);
            return {
                data: undefined,
                isLoading: false,
                hasError: true,
                error: query.error,
            };
        }
        if (query.data === undefined) {
            debugLog("useGetCumulativeDailyNetDeposits", `data is undefined but loading and error are false with ${timeFrame} for ${groupId}`);
            return {
                data: undefined,
                isLoading: false,
                hasError: true,
                error: new Error("Unknown error"),
            };
        }
        const generatedData = memoizedGenerateCumulativeDailyNetDeposits(query.data, timeFrameDates.data.startDate, timeFrameDates.data.endDate);
        return {
            data: generatedData,
            isLoading: false,
            hasError: false,
        };
    }, [
        groupId,
        query.data,
        query.error,
        query.isError,
        query.isLoading,
        timeFrame,
        timeFrameDates,
    ]);
    return result;
}
//# sourceMappingURL=useGetNetDeposits.js.map